import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from 'react'
import { useHitTarget, type HitTargetSize } from '../responsive/hitTarget'
import './field.css'

/**
 * `size` is omitted from the native attributes deliberately.
 *
 * `<input size>` is the legacy presentational attribute that sets width in
 * CHARACTERS. Intersecting it with our own `size` gave `number & HitTargetSize`
 * — i.e. `never` — and every call site failed with "Type 'string' is not
 * assignable to type 'undefined'", which reads like a bug in the caller.
 *
 * Omitted rather than worked around: nothing in this repo sets it, CSS
 * supersedes it, and `Button` already spells this concept `size`. Renaming
 * ours to dodge the collision would make the two primitives disagree about the
 * name of the same idea.
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  /**
   * Forwarded to the input. React 19 passes `ref` as an ordinary prop, so this
   * needs no `forwardRef` — but `InputHTMLAttributes` does not declare it, so
   * without this line a caller cannot move focus to the control this component
   * owns. #171 needs it: every send disables the button that was pressed, and a
   * browser blurs a disabled element to `<body>`.
   */
  ref?: Ref<HTMLInputElement>
  label: string
  /** The message M1-R2 wants ON the field. Presence is what marks it invalid. */
  error?: string
  hint?: string
  /**
   * A control that shares the label's row, right-aligned — the design's
   * `Forgot?` beside `Password`.
   *
   * A prop rather than something a caller lays out itself, because the
   * alternative is a caller wrapping `<Field>` in its own flex row and
   * re-implementing the label — which detaches `htmlFor` from the input and is
   * how a field silently stops being labelled. The row belongs to the control.
   *
   * Rendered OUTSIDE the `<label>`: a link inside a label is a control inside
   * a control, and clicking it would also focus the input.
   */
  labelAction?: ReactNode
  /**
   * The control's density. `page` is the default and is what every field
   * shipped before issue 271.
   *
   * `form` is the pre-login density: the design draws those inputs
   * `padding:12px 14px; font-size:13.5px`, landing at ~42px, where `page`
   * carries no vertical padding and sits on the 33px hit-target floor — about
   * 10px shorter than the comp on desktop, and invisible on a phone because
   * the coarse floor of 44 happens to land near the design's 42.
   *
   * A prop rather than a global change to `.field__input`: 11 of the 12 uses
   * of `12px 14px` on an input in the whole design source are on the pre-login
   * screens, and the others use `9px 12px`. This is an auth density, not a new
   * default.
   */
  size?: HitTargetSize
}

/**
 * The id of the element carrying a `Field`'s error sentence — issue 386.
 *
 * Exported because a caller may need to point something ELSE at that sentence:
 * `InviteDialog`'s submit is `aria-disabled` on the very condition the field is
 * explaining, and a control a screen-reader user can reach and be told is
 * unavailable should say what would make it available. Derived here rather than
 * spelled out at the call site so the two cannot drift — a describedby pointing
 * at a stale id is silence, which is exactly what it was added to end.
 *
 * Only meaningful while `error` is set: the element is not rendered otherwise,
 * and a caller binding this unconditionally would point at nothing.
 */
export function fieldErrorId(inputId: string) {
  return `${inputId}-error`
}

/**
 * A labelled text input.
 *
 * **The skin is in field.css**, its own sheet — see the header there for why it
 * is not in `primitives.css`. An inline `border-color` would beat the
 * `:focus-visible` rule and the focus ring would be silently inert — the trap
 * `Button` documents and this repo has hit twice.
 *
 * `min-height` is the one inline declaration, for the same reason it is inline
 * on `Button`: a caller must not be able to shrink a control under the
 * documented page target, and inline is what wins that argument.
 *
 * **`useHitTarget`, not `HIT_TARGET.page`.** M0-R29 raises the target to 44px
 * below 640px, and the constant is a flat 33 — which is what `Button` still
 * writes, tracked as #90 because changing it there moves every button in the
 * app. `Field` has no callers, so there is no migration to stage and no reason
 * to ship the second site of a known defect. `hitTarget.ts` says the point of
 * centralising the map was that #90 "would have had to be applied twice without
 * noticing the second"; this is that second site, noticed.
 *
 * **`useId`, not a module counter.** Two `<Field label="Email">` on one page
 * with a shared id makes the second label address the first input, which is a
 * bug only a screen-reader user meets.
 */
export function Field({
  label,
  error,
  hint,
  labelAction,
  size = 'page',
  id,
  className,
  style,
  ...rest
}: Props) {
  const auto = useId()
  const minHeight = useHitTarget(size)
  const inputId = id ?? auto
  const errorId = fieldErrorId(inputId)
  const hintId = `${inputId}-hint`

  // Error first: it is the more urgent of the two and describedby is read in
  // order. Joined rather than replaced, so a field carrying both keeps both.
  // `undefined` rather than '' — an empty attribute is still an attribute, and
  // it points at nothing.
  //
  // A caller's own `aria-describedby` is preserved rather than overwritten.
  // `{...rest}` spreads first and the attribute below is written after, so the
  // later key won even when it was `undefined` — a caller wiring their own
  // description got silence and no warning. Same treatment `id` already had.
  const { 'aria-describedby': callerDescribedBy, ...inputProps } = rest
  const describedBy = [callerDescribedBy, error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="field">
      {/* The label keeps its own element when it is alone, so the common case
          adds no wrapper and no rule. The row appears only when something has
          to share it. */}
      {labelAction ? (
        <span className="field__label-row">
          <label className="field__label" htmlFor={inputId}>
            {label}
          </label>
          {labelAction}
        </span>
      ) : (
        <label className="field__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <input
        {...inputProps}
        id={inputId}
        className={[
          'field__input',
          `field__input--${size}`,
          error ? 'field__input--invalid' : null,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        style={{ ...style, minHeight }}
      />
      {hint && (
        <span id={hintId} className="field__hint">
          {hint}
        </span>
      )}
      {error && (
        // role="alert" on the message, not the wrapper: an assertive region
        // should wrap the sentence, matching what `Async`'s error branch does.
        <span id={errorId} className="field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  )
}
